import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FileText } from 'lucide-react';
import { KeyEvent, KeyHolder, keyHolderKindLabel, RegisterKey } from '../../types/keyRegister';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { useStorageUrls } from './useStorageUrls';
import { LOG_PREVIEW_COUNT, logHeadline, logMarkerClass } from './keyRegisterLabels';

interface KeyHistoryProps {
    /** Oldest first, as stored; shown newest first. */
    log: RegisterKey['log'];
    holderName: (holder: KeyHolder | undefined) => string;
    actorName: (uid: string) => string;
    getReceiptUrl: (storagePath: string) => Promise<string>;
}

/** "2026-10-08 14:03:11" → "2026-10-08 14:03". */
const shortWhen = (when: string): string => when.slice(0, 16);

/** "2026-09-01 (registrerad 2026-10-29 14:03)" when recorded on a later day, else the recording time. */
const eventDate = (event: Pick<KeyEvent, 'effectiveDate' | 'when'>): string =>
    event.effectiveDate === event.when.slice(0, 10)
        ? shortWhen(event.when)
        : `${event.effectiveDate} (registrerad ${shortWhen(event.when)})`;

/** The key's history: a timeline, newest first, the latest few shown until "Visa äldre". */
const KeyHistory: React.FC<KeyHistoryProps> = ({ log, holderName, actorName, getReceiptUrl }) => {
    const receiptUrls = useStorageUrls(log.map(event => event.receipt?.storagePath).filter((p): p is string => !!p), getReceiptUrl);
    const [showAll, setShowAll] = useState(false);
    const tenantTerm = useTenantTerm();
    const newestFirst = [...log].reverse();
    const hiddenCount = Math.max(0, newestFirst.length - LOG_PREVIEW_COUNT);
    const visible = showAll ? newestFirst : newestFirst.slice(0, LOG_PREVIEW_COUNT);

    return (
        <section className="key-detail-log">
            <h3>Historik</h3>
            <ol className="key-log">
                {visible.map(event => {
                    const { label, preposition } = logHeadline(event);
                    const receiptUrl = event.receipt ? receiptUrls[event.receipt.storagePath] : undefined;
                    return (
                        <li key={event.key} className="key-log-item" data-testid={`key-log-${event.key}`}>
                            <span className={`key-log-marker ${logMarkerClass(event.type)}`} aria-hidden="true" data-testid="key-log-marker" />
                            <div className="key-log-headline">
                                <strong>{label}</strong>
                                {event.holder && (
                                    <>
                                        {preposition ? ` ${preposition} ` : ' · '}
                                        {holderName(event.holder)}
                                        <span className="key-log-kind"> ({keyHolderKindLabel(event.holder.kind, tenantTerm).toLowerCase()})</span>
                                    </>
                                )}
                            </div>
                            <div className="key-log-meta">{actorName(event.actor)} · {eventDate(event)}</div>
                            {(event.dueDate || event.receipt) && (
                                <div className="key-log-extras">
                                    {event.dueDate && <span>Åter senast {event.dueDate}</span>}
                                    {event.receipt && (receiptUrl ? (
                                        <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="key-log-receipt">
                                            <FileText size={14} className="me-1" aria-hidden="true" />Visa kvitto
                                        </a>
                                    ) : (
                                        <span className="key-log-receipt text-muted">
                                            <FileText size={14} className="me-1" aria-hidden="true" />Kvitto
                                        </span>
                                    ))}
                                </div>
                            )}
                            {event.note && <blockquote className="key-log-note">{event.note}</blockquote>}
                        </li>
                    );
                })}
            </ol>
            {hiddenCount > 0 && (
                <Button variant="link" className="key-log-more" onClick={() => setShowAll(value => !value)} aria-expanded={showAll}>
                    {showAll ? 'Visa färre' : `Visa ${hiddenCount} äldre`}
                </Button>
            )}
        </section>
    );
};

export default KeyHistory;
